/**
 * HSECreateModal — Reusable, schema-driven creation form for the HSE module
 * sub-tabs that previously had no author UI:
 *   • Permit  → POST /api/v1/projects/{pid}/hse/permits
 *   • Environmental Compliance → POST .../hse/environmental/compliance
 *   • Inspection → POST .../hse/inspections
 *   • Observation → POST .../hse/observations
 *   • Toolbox Talk → POST .../hse/tbt
 *   • Training → POST .../hse/training
 *   • Emergency Drill → POST .../hse/emergency/drills
 *
 * Usage:
 *   <HSECreateModal kind="permit" projectId={pid} onCreated={refresh} onClose={...} />
 */
import { useState } from 'react';
import { X, Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';

const inputClass = "w-full rounded-lg px-3 py-2 text-sm outline-none transition-all";
const inputStyle = { backgroundColor: 'var(--hover-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' };

const AREAS = ['Unit 1', 'Pipe Rack PR-01', 'Tank Farm', 'Substation', 'Control Room', 'Fabrication Yard', 'Laydown Area', 'Admin Block'];
const PERMIT_TYPES = [
  { code: 'HW',   value: 'HOT_WORK',         label: 'Hot Work' },
  { code: 'CS',   value: 'CONFINED_SPACE',   label: 'Confined Space' },
  { code: 'WH',   value: 'WORKING_AT_HEIGHT', label: 'Working at Height' },
  { code: 'EX',   value: 'EXCAVATION',       label: 'Excavation' },
  { code: 'LO',   value: 'LIFTING_OPERATION', label: 'Lifting Operation' },
  { code: 'EL',   value: 'ELECTRICAL',       label: 'Electrical' },
  { code: 'RAD',  value: 'RADIOGRAPHY',      label: 'Radiography' },
  { code: 'GEN',  value: 'GENERAL',          label: 'General' },
];
const ENV_CATEGORIES = ['AIR_EMISSIONS', 'WATER_DISCHARGE', 'NOISE', 'WASTE_MANAGEMENT', 'SOIL_GROUNDWATER', 'BIODIVERSITY', 'CHEMICAL_STORAGE'];
const ENV_FREQUENCIES = ['DAILY', 'WEEKLY', 'MONTHLY', 'QUARTERLY', 'SEMI_ANNUAL', 'ANNUAL'];
const ENV_STATUSES = ['COMPLIANT', 'AT_RISK', 'NON_COMPLIANT', 'OVERDUE'];
const INSPECTION_TYPES = ['SAFETY_AUDIT', 'PLANNED_WALKTHROUGH', 'EQUIPMENT_INSPECTION', 'SCAFFOLD_INSPECTION', 'EXCAVATION_INSPECTION', 'FIRE_SYSTEM', 'PPE_AUDIT', 'LIFTING_GEAR'];
const OBS_TYPES = ['SAFE_ACT', 'UNSAFE_ACT', 'UNSAFE_CONDITION', 'SAFE_CONDITION', 'POSITIVE_REINFORCEMENT', 'SAFETY_CONVERSATION'];
const TRAINING_TYPES = ['INDUCTION', 'TASK_SPECIFIC', 'REFRESHER', 'PERMIT_SPECIFIC', 'EMERGENCY_RESPONSE', 'FIRST_AID', 'WORK_AT_HEIGHT', 'CONFINED_SPACE'];
const DRILL_TYPES = ['FIRE_EVACUATION', 'MEDICAL_EMERGENCY', 'GAS_LEAK', 'SPILL_RESPONSE', 'CONFINED_SPACE_RESCUE', 'FALL_RESCUE'];

// Per-kind schema: title, fields, default values, POST endpoint suffix.
const SCHEMAS = {
  permit: {
    title: 'Permit to Work',
    endpoint: 'permits',
    initial: () => ({
      permitTypeCode: 'HW',
      permitType: 'HOT_WORK',
      workDescription: '',
      area: '',
      contractorName: '',
      validFrom: new Date().toISOString().split('T')[0],
      validTo: new Date(Date.now() + 86400000).toISOString().split('T')[0],
    }),
    fields: (form, set) => [
      ['Permit Type', true,
        <select className={inputClass} style={inputStyle} value={form.permitTypeCode}
          onChange={e => {
            const t = PERMIT_TYPES.find(p => p.code === e.target.value);
            set({ permitTypeCode: t.code, permitType: t.value });
          }}>
          {PERMIT_TYPES.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
        </select>],
      ['Work Description', true,
        <input className={inputClass} style={inputStyle} value={form.workDescription}
          onChange={e => set({ workDescription: e.target.value })} placeholder="Brief description of work" />],
      ['Area', true,
        <select className={inputClass} style={inputStyle} value={form.area}
          onChange={e => set({ area: e.target.value })}>
          <option value="">— select —</option>
          {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
        </select>],
      ['Contractor', false,
        <input className={inputClass} style={inputStyle} value={form.contractorName}
          onChange={e => set({ contractorName: e.target.value })} placeholder="Contractor name" />],
      ['Valid From', true,
        <input type="date" className={inputClass} style={inputStyle} value={form.validFrom}
          onChange={e => set({ validFrom: e.target.value })} />],
      ['Valid To', true,
        <input type="date" className={inputClass} style={inputStyle} value={form.validTo}
          onChange={e => set({ validTo: e.target.value })} />],
    ],
    required: ['workDescription', 'area', 'validFrom', 'validTo'],
  },
  environmental: {
    title: 'Environmental Compliance Item',
    endpoint: 'environmental/compliance',
    initial: () => ({
      complianceCode: '',
      complianceName: '',
      complianceCategory: 'AIR_EMISSIONS',
      frequency: 'MONTHLY',
      nextDueDate: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
      currentStatus: 'COMPLIANT',
      responsiblePerson: '',
    }),
    fields: (form, set) => [
      ['Code', true,
        <input className={inputClass} style={inputStyle} value={form.complianceCode}
          onChange={e => set({ complianceCode: e.target.value.toUpperCase() })} placeholder="e.g. ENV-AIR-001" />],
      ['Name', true,
        <input className={inputClass} style={inputStyle} value={form.complianceName}
          onChange={e => set({ complianceName: e.target.value })} placeholder="Stack emissions monitoring" />],
      ['Category', true,
        <select className={inputClass} style={inputStyle} value={form.complianceCategory}
          onChange={e => set({ complianceCategory: e.target.value })}>
          {ENV_CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
        </select>],
      ['Frequency', true,
        <select className={inputClass} style={inputStyle} value={form.frequency}
          onChange={e => set({ frequency: e.target.value })}>
          {ENV_FREQUENCIES.map(f => <option key={f} value={f}>{f}</option>)}
        </select>],
      ['Next Due Date', true,
        <input type="date" className={inputClass} style={inputStyle} value={form.nextDueDate}
          onChange={e => set({ nextDueDate: e.target.value })} />],
      ['Status', false,
        <select className={inputClass} style={inputStyle} value={form.currentStatus}
          onChange={e => set({ currentStatus: e.target.value })}>
          {ENV_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>],
      ['Responsible Person', false,
        <input className={inputClass} style={inputStyle} value={form.responsiblePerson}
          onChange={e => set({ responsiblePerson: e.target.value })} placeholder="Owner / role" />],
    ],
    required: ['complianceCode', 'complianceName'],
  },
  inspection: {
    title: 'Inspection',
    endpoint: 'inspections',
    initial: () => ({
      inspectionType: 'SAFETY_AUDIT',
      inspectionDate: new Date().toISOString().split('T')[0],
      area: '',
      inspectedByName: '',
      complianceScore: 90,
      overallRating: 'Good',
      nonConformingItems: 0,
      remarks: '',
    }),
    fields: (form, set) => [
      ['Type', true,
        <select className={inputClass} style={inputStyle} value={form.inspectionType}
          onChange={e => set({ inspectionType: e.target.value })}>
          {INSPECTION_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>],
      ['Date', true,
        <input type="date" className={inputClass} style={inputStyle} value={form.inspectionDate}
          onChange={e => set({ inspectionDate: e.target.value })} />],
      ['Area', true,
        <select className={inputClass} style={inputStyle} value={form.area}
          onChange={e => set({ area: e.target.value })}>
          <option value="">— select —</option>
          {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
        </select>],
      ['Inspector', true,
        <input className={inputClass} style={inputStyle} value={form.inspectedByName}
          onChange={e => set({ inspectedByName: e.target.value })} placeholder="Inspector name" />],
      ['Score (%)', false,
        <input type="number" min={0} max={100} className={inputClass} style={inputStyle} value={form.complianceScore}
          onChange={e => set({ complianceScore: Number(e.target.value) })} />],
      ['Rating', false,
        <select className={inputClass} style={inputStyle} value={form.overallRating}
          onChange={e => set({ overallRating: e.target.value })}>
          {['Excellent', 'Good', 'Acceptable', 'Poor'].map(r => <option key={r}>{r}</option>)}
        </select>],
      ['Non-Conforming Items', false,
        <input type="number" min={0} className={inputClass} style={inputStyle} value={form.nonConformingItems}
          onChange={e => set({ nonConformingItems: Number(e.target.value) })} />],
      ['Remarks', false,
        <textarea className={inputClass} style={inputStyle} rows={2} value={form.remarks}
          onChange={e => set({ remarks: e.target.value })} placeholder="Observations / corrective actions" />],
    ],
    required: ['area', 'inspectedByName'],
  },
  observation: {
    title: 'Observation',
    endpoint: 'observations',
    initial: () => ({
      observationDate: new Date().toISOString().split('T')[0],
      observationType: 'SAFE_ACT',
      area: '',
      activityObserved: '',
      contractorName: '',
      observedByName: '',
      description: '',
    }),
    fields: (form, set) => [
      ['Date', true,
        <input type="date" className={inputClass} style={inputStyle} value={form.observationDate}
          onChange={e => set({ observationDate: e.target.value })} />],
      ['Type', true,
        <select className={inputClass} style={inputStyle} value={form.observationType}
          onChange={e => set({ observationType: e.target.value })}>
          {OBS_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>],
      ['Area', true,
        <select className={inputClass} style={inputStyle} value={form.area}
          onChange={e => set({ area: e.target.value })}>
          <option value="">— select —</option>
          {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
        </select>],
      ['Activity Observed', true,
        <input className={inputClass} style={inputStyle} value={form.activityObserved}
          onChange={e => set({ activityObserved: e.target.value })} placeholder="e.g. Welding, Scaffolding erection" />],
      ['Contractor', false,
        <input className={inputClass} style={inputStyle} value={form.contractorName}
          onChange={e => set({ contractorName: e.target.value })} placeholder="Contractor" />],
      ['Observer', true,
        <input className={inputClass} style={inputStyle} value={form.observedByName}
          onChange={e => set({ observedByName: e.target.value })} placeholder="Observer name" />],
      ['Description', false,
        <textarea className={inputClass} style={inputStyle} rows={2} value={form.description}
          onChange={e => set({ description: e.target.value })} placeholder="What did you observe?" />],
    ],
    required: ['area', 'activityObserved', 'observedByName'],
  },
  tbt: {
    title: 'Toolbox Talk',
    endpoint: 'tbt',
    initial: () => ({
      tbtDate: new Date().toISOString().split('T')[0],
      topic: '',
      conductedByName: '',
      area: '',
      attendeesCount: 0,
      durationMinutes: 15,
      remarks: '',
    }),
    fields: (form, set) => [
      ['Date', true,
        <input type="date" className={inputClass} style={inputStyle} value={form.tbtDate}
          onChange={e => set({ tbtDate: e.target.value })} />],
      ['Topic', true,
        <input className={inputClass} style={inputStyle} value={form.topic}
          onChange={e => set({ topic: e.target.value })} placeholder="e.g. Working at heights" />],
      ['Conducted By', true,
        <input className={inputClass} style={inputStyle} value={form.conductedByName}
          onChange={e => set({ conductedByName: e.target.value })} placeholder="Presenter" />],
      ['Area', false,
        <select className={inputClass} style={inputStyle} value={form.area}
          onChange={e => set({ area: e.target.value })}>
          <option value="">— select —</option>
          {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
        </select>],
      ['Attendees', false,
        <input type="number" min={0} className={inputClass} style={inputStyle} value={form.attendeesCount}
          onChange={e => set({ attendeesCount: Number(e.target.value) })} />],
      ['Duration (min)', false,
        <input type="number" min={1} className={inputClass} style={inputStyle} value={form.durationMinutes}
          onChange={e => set({ durationMinutes: Number(e.target.value) })} />],
      ['Remarks', false,
        <textarea className={inputClass} style={inputStyle} rows={2} value={form.remarks}
          onChange={e => set({ remarks: e.target.value })} placeholder="Key takeaways" />],
    ],
    required: ['topic', 'conductedByName'],
  },
  training: {
    title: 'Training Session',
    endpoint: 'training',
    initial: () => ({
      trainingDate: new Date().toISOString().split('T')[0],
      trainingType: 'INDUCTION',
      trainingTitle: '',
      trainerName: '',
      attendeesCount: 0,
      passedCount: 0,
      durationHours: 4,
    }),
    fields: (form, set) => [
      ['Date', true,
        <input type="date" className={inputClass} style={inputStyle} value={form.trainingDate}
          onChange={e => set({ trainingDate: e.target.value })} />],
      ['Type', true,
        <select className={inputClass} style={inputStyle} value={form.trainingType}
          onChange={e => set({ trainingType: e.target.value })}>
          {TRAINING_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>],
      ['Title', true,
        <input className={inputClass} style={inputStyle} value={form.trainingTitle}
          onChange={e => set({ trainingTitle: e.target.value })} placeholder="e.g. Safe scaffolding practices" />],
      ['Trainer', true,
        <input className={inputClass} style={inputStyle} value={form.trainerName}
          onChange={e => set({ trainerName: e.target.value })} placeholder="Trainer name" />],
      ['Attendees', false,
        <input type="number" min={0} className={inputClass} style={inputStyle} value={form.attendeesCount}
          onChange={e => set({ attendeesCount: Number(e.target.value) })} />],
      ['Passed', false,
        <input type="number" min={0} className={inputClass} style={inputStyle} value={form.passedCount}
          onChange={e => set({ passedCount: Number(e.target.value) })} />],
      ['Duration (hours)', false,
        <input type="number" min={0} step={0.5} className={inputClass} style={inputStyle} value={form.durationHours}
          onChange={e => set({ durationHours: Number(e.target.value) })} />],
    ],
    required: ['trainingTitle', 'trainerName'],
  },
  drill: {
    title: 'Emergency Drill',
    endpoint: 'emergency/drills',
    initial: () => ({
      drillDate: new Date().toISOString().split('T')[0],
      drillType: 'FIRE_EVACUATION',
      scenario: '',
      conductedByName: '',
      attendeesCount: 0,
      durationMinutes: 30,
      remarks: '',
    }),
    fields: (form, set) => [
      ['Date', true,
        <input type="date" className={inputClass} style={inputStyle} value={form.drillDate}
          onChange={e => set({ drillDate: e.target.value })} />],
      ['Type', true,
        <select className={inputClass} style={inputStyle} value={form.drillType}
          onChange={e => set({ drillType: e.target.value })}>
          {DRILL_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>],
      ['Scenario', true,
        <input className={inputClass} style={inputStyle} value={form.scenario}
          onChange={e => set({ scenario: e.target.value })} placeholder="Mock fire in Unit 1 control room" />],
      ['Conducted By', true,
        <input className={inputClass} style={inputStyle} value={form.conductedByName}
          onChange={e => set({ conductedByName: e.target.value })} placeholder="Drill coordinator" />],
      ['Attendees', false,
        <input type="number" min={0} className={inputClass} style={inputStyle} value={form.attendeesCount}
          onChange={e => set({ attendeesCount: Number(e.target.value) })} />],
      ['Duration (min)', false,
        <input type="number" min={1} className={inputClass} style={inputStyle} value={form.durationMinutes}
          onChange={e => set({ durationMinutes: Number(e.target.value) })} />],
      ['Remarks', false,
        <textarea className={inputClass} style={inputStyle} rows={2} value={form.remarks}
          onChange={e => set({ remarks: e.target.value })} placeholder="Lessons learned / response time" />],
    ],
    required: ['scenario', 'conductedByName'],
  },
};

export default function HSECreateModal({ kind, projectId, onCreated, onClose }) {
  const schema = SCHEMAS[kind];
  const [form, setForm] = useState(schema.initial());
  const [saving, setSaving] = useState(false);

  if (!schema) {
    return null;
  }

  const set = (patch) => setForm(prev => ({ ...prev, ...patch }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    for (const f of schema.required) {
      if (!form[f]) {
        return toast.error(`${f} is required`);
      }
    }
    setSaving(true);
    try {
      const r = await api.post(`/v1/projects/${projectId}/hse/${schema.endpoint}`, form);
      toast.success(`${schema.title} created`);
      onCreated?.(r.data);
      onClose?.();
    } catch (err) {
      toast.error(`Failed: ${err?.response?.data?.detail || err.message}`);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
         data-testid={`hse-create-modal-${kind}`}
         onClick={onClose}>
      <div className="w-full max-w-2xl rounded-xl shadow-2xl"
           style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}
           onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3"
             style={{ borderBottom: '1px solid var(--border-color)' }}>
          <h3 className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>New {schema.title}</h3>
          <button onClick={onClose} className="p-1 rounded hover:opacity-70"
                  style={{ color: 'var(--text-muted)' }}
                  data-testid={`hse-create-close-${kind}`}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto">
            {schema.fields(form, set).map(([label, required, control], idx) => {
              const isWide = control?.type === 'textarea' || (typeof control?.props?.rows === 'number');
              return (
                <div key={idx} className={isWide ? 'md:col-span-2' : ''}>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                    {label}{required && <span className="text-red-500 ml-0.5">*</span>}
                  </label>
                  {control}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 px-5 py-3"
               style={{ borderTop: '1px solid var(--border-color)' }}>
            <Button type="button" variant="outline" size="sm" onClick={onClose}
                    data-testid={`hse-create-cancel-${kind}`}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={saving}
                    data-testid={`hse-create-submit-${kind}`}>
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Create
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
